import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, OnInit, signal, EnvironmentInjector } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Rider, RiderService, RiderDocument } from '../../../services/riders/rider.service';
import { SnackbarService } from '../../../services/design/snackbar.service';
import { ModalService } from '../../../services/design/modal.service';
import { ConfirmModalComponent } from '../../../Design/confirm-modal.component';

@Component({
  selector: 'app-rider-verification-detail',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div class="bg-base-100 w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-3xl shadow-2xl flex flex-col border border-base-200">
        <!-- Header -->
        <div class="p-6 border-b border-base-200 flex items-center justify-between bg-base-100/50">
          <div class="flex items-center gap-4">
            <div class="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-bold text-2xl overflow-hidden border-2 border-primary/20">
              @if (rider.profile_photo_url) {
                <img [src]="rider.profile_photo_url" alt="Profile" class="w-full h-full object-cover">
              } @else {
                {{ rider.full_name[0] }}
              }
            </div>
            <div>
              <h3 class="text-xl font-bold">Review Documents</h3>
              <p class="text-sm opacity-60 flex items-center gap-2">
                {{ rider.full_name }} 
                <span class="w-1 h-1 rounded-full bg-base-content/20"></span>
                <span class="font-mono text-xs">{{ rider.code || rider.id.slice(0,8) }}</span>
                <span class="badge badge-sm" [class.badge-warning]="rider.verification_status === 'pending'" [class.badge-success]="rider.verification_status === 'approved'" [class.badge-error]="rider.verification_status === 'rejected'">
                  {{ rider.verification_status }}
                </span>
              </p>
            </div>
          </div>
          <button class="btn btn-ghost btn-circle" (click)="closed.emit()">
            <i class="fa-solid fa-times text-xl"></i>
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-8 space-y-10">
           <!-- Overview Cards -->
           <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div class="bg-base-200/50 p-5 rounded-2xl border border-white/5">
                 <p class="text-[10px] font-black uppercase opacity-40 tracking-widest mb-1">Onboarding Step</p>
                 <p class="text-sm font-bold text-primary">{{ rider.onboarding_step }}</p>
              </div>
              <div class="bg-base-200/50 p-5 rounded-2xl border border-white/5">
                 <p class="text-[10px] font-black uppercase opacity-40 tracking-widest mb-1">Vehicle Type</p>
                 <p class="text-sm font-bold capitalize">{{ (rider.vehicle_type || '').replace('_', ' ') }}</p>
              </div>
              <div class="bg-base-200/50 p-5 rounded-2xl border border-white/5">
                 <p class="text-[10px] font-black uppercase opacity-40 tracking-widest mb-1">Phone / User ID</p>
                 <p class="text-xs font-mono truncate">{{ rider.user_id }}</p>
              </div>
              <div class="bg-base-200/50 p-5 rounded-2xl border border-white/5">
                 <p class="text-[10px] font-black uppercase opacity-40 tracking-widest mb-1">Created At</p>
                 <p class="text-sm font-bold">{{ rider.created_at | date:'mediumDate' }}</p>
              </div>
           </div>

           <!-- Details Grid -->
           <div class="grid grid-cols-1 lg:grid-cols-4 gap-10">
              <!-- Left Col: Information -->
              <div class="lg:col-span-1 space-y-8">
                 <!-- Bank Details -->
                 <section class="space-y-4">
                    <h4 class="text-xs font-black uppercase tracking-[0.2em] opacity-30 flex items-center gap-2">
                       <i class="fa-solid fa-bank"></i> Settlement
                    </h4>
                    <div class="bg-base-200/30 rounded-3xl p-6 border border-white/5 space-y-4">
                       <div class="space-y-1">
                          <p class="text-[10px] opacity-40 font-bold">Account Holder</p>
                          <p class="text-sm font-medium">{{ rider.bank_account?.account_holder_name || 'Not Provided' }}</p>
                       </div>
                       <div class="space-y-1">
                          <p class="text-[10px] opacity-40 font-bold">Account Number</p>
                          <p class="text-sm font-mono">{{ rider.bank_account?.account_number || 'N/A' }}</p>
                       </div>
                       <div class="space-y-1">
                          <p class="text-[10px] opacity-40 font-bold">IFSC Code</p>
                          <p class="text-sm font-mono uppercase">{{ rider.bank_account?.ifsc || 'N/A' }}</p>
                       </div>
                       <div class="pt-3 border-t border-white/5">
                          <p class="text-[10px] opacity-40 font-bold">UPI ID</p>
                          <p class="text-sm font-bold text-success">{{ rider.bank_account?.upi_id || 'None' }}</p>
                       </div>
                    </div>
                 </section>

                 <!-- Wallet Info -->
                 <section class="space-y-4">
                    <h4 class="text-xs font-black uppercase tracking-[0.2em] opacity-30 flex items-center gap-2">
                       <i class="fa-solid fa-wallet"></i> Wallet
                    </h4>
                    <div class="bg-primary/5 rounded-3xl p-6 border border-primary/10 space-y-4">
                       <div class="flex justify-between items-center">
                          <div>
                             <p class="text-[10px] opacity-60 font-bold uppercase">Balance</p>
                             <p class="text-2xl font-black">₹{{ rider.wallet?.balance || 0 }}</p>
                          </div>
                          @if (rider.wallet?.pending_balance) {
                            <div class="text-right">
                               <p class="text-[10px] opacity-60 font-bold uppercase text-warning">Pending</p>
                               <p class="text-sm font-bold italic text-warning">₹{{ rider.wallet?.pending_balance }}</p>
                            </div>
                          }
                       </div>
                       <div class="pt-3 border-t border-primary/10">
                          <p class="text-[10px] opacity-60 font-bold uppercase">Lifetime Earnings</p>
                          <p class="text-sm font-bold">₹{{ rider.wallet?.lifetime_earning || 0 }}</p>
                       </div>
                    </div>
                 </section>
              </div>

              <!-- Right Col: Documents -->
              <div class="lg:col-span-3 space-y-6">
                 <h4 class="text-xs font-black uppercase tracking-[0.2em] opacity-30 flex items-center gap-2">
                    <i class="fa-solid fa-file-shield"></i> Rider Documents
                 </h4>
                 
                 <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    @for (docType of expectedDocTypes; track docType.key) {
                      @let doc = getDoc(docType.key);
                      <div class="bg-base-200/30 rounded-3xl p-4 border border-white/5 flex flex-col gap-3 group">
                        <div class="flex items-center justify-between">
                           <span class="text-[10px] font-black uppercase opacity-50">{{ docType.label }}</span>
                           @if (doc) {
                             <span class="badge badge-sm font-bold uppercase" 
                                   [class.badge-success]="doc.status === 'approved'"
                                   [class.badge-error]="doc.status === 'rejected'"
                                   [class.badge-warning]="doc.status === 'pending'">
                               {{ doc.status }}
                             </span>
                           } @else {
                             <span class="badge badge-sm badge-ghost opacity-30">Missing</span>
                           }
                        </div>

                        <div class="aspect-video bg-base-300 rounded-2xl overflow-hidden border border-base-300 relative group">
                           @if (doc?.url) {
                             <img [src]="doc?.url" [alt]="docType.label" 
                                  class="w-full h-full object-cover transition duration-500 cursor-pointer"
                                  (click)="openImage(doc?.url!)"
                                  (keydown.enter)="openImage(doc?.url!)"
                                  tabindex="0"
                                  role="button">
                             <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition pointer-events-none">
                                <span class="text-white font-bold"><i class="fa-solid fa-expand text-2xl"></i></span>
                             </div>
                           } @else {
                             <div class="w-full h-full flex flex-col items-center justify-center opacity-20">
                                <i class="fa-solid fa-image text-3xl mb-1"></i>
                                <span class="text-[10px] font-bold">No File</span>
                             </div>
                           }
                        </div>
                        
                        @if (doc?.doc_number) {
                          <p class="text-[10px] font-mono opacity-50 truncate px-1"># {{ doc?.doc_number }}</p>
                        }
                      </div>
                    }
                 </div>
              </div>
           </div>

           <!-- Warning box -->
           <div class="bg-warning/10 border border-warning/20 p-5 rounded-[2rem] flex gap-5">
              <div class="w-10 h-10 rounded-xl bg-warning/20 flex items-center justify-center text-warning shrink-0">
                 <i class="fa-solid fa-shield-halved"></i>
              </div>
              <p class="text-[13px] text-warning-content font-medium leading-relaxed">
                Review all documents and settlement details carefully. 
                Approve only if documents are clear and match the profile information.
              </p>
           </div>
        </div>

        <!-- Footer -->
        <div class="p-6 border-t border-base-200 bg-base-200/30 flex items-center justify-between gap-4">
          <button class="btn btn-ghost px-8" (click)="closed.emit()">Cancel</button>
          
          <div class="flex gap-3">
             <button class="btn btn-error btn-outline px-8" (click)="reject()"> Reject Application </button>
             <button class="btn btn-primary px-10 shadow-lg shadow-primary/20 h-12" (click)="approve()">
                Approve Rider
             </button>
          </div>
        </div>
      </div>
    </div>

    @if (selectedImage()) {
      <div class="fixed inset-0 z-[60] bg-black/90 backdrop-blur-xl p-8 flex flex-col items-center justify-center" 
           (click)="selectedImage.set(null)"
           (keydown.escape)="selectedImage.set(null)"
           tabindex="0"
           role="button">
        <button class="absolute top-8 right-8 btn btn-circle btn-ghost text-white" aria-label="Close">
          <i class="fa-solid fa-times text-2xl"></i>
        </button>
        <img [src]="selectedImage()" alt="Enlarged Document" class="max-w-full max-h-full object-contain rounded-xl shadow-2xl">
      </div>
    }

    <!-- Rejection Remark Modal -->
    @if (showRejectModal()) {
      <div class="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm p-4 flex items-center justify-center">
        <div class="bg-base-100 w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl border border-white/10 space-y-6">
           <div class="flex items-center gap-4 text-error">
              <div class="w-12 h-12 rounded-2xl bg-error/10 flex items-center justify-center">
                 <i class="fa-solid fa-circle-exclamation text-xl"></i>
              </div>
              <h3 class="text-xl font-bold">Reject Application</h3>
           </div>

           <p class="text-sm opacity-60">Please provide a clear reason for rejection. This will be shared with the rider to help them correct their application.</p>

           <div class="space-y-2">
              <label for="rejection-reason" class="text-[10px] font-black uppercase tracking-widest opacity-40 px-1">Rejection Reason</label>
              <textarea 
                id="rejection-reason"
                [(ngModel)]="rejectionReason"
                placeholder="e.g. Aadhaar image is blurry, please re-upload a clearer photo"
                class="textarea textarea-bordered w-full h-32 rounded-2xl bg-base-200/50 border-none focus:ring-2 focus:ring-error/20 transition-all font-medium"
              ></textarea>
           </div>

           <div class="flex gap-3">
              <button class="btn btn-ghost flex-1 rounded-xl" (click)="showRejectModal.set(false)">Cancel</button>
              <button 
                class="btn btn-error flex-[2] rounded-xl shadow-lg shadow-error/20" 
                [disabled]="!rejectionReason().trim() || isSubmitting()"
                (click)="confirmReject()"
              >
                @if (isSubmitting()) {
                  <span class="loading loading-spinner loading-xs"></span>
                }
                Confirm Rejection
              </button>
           </div>
        </div>
      </div>
    }
  `,
})
export class RiderVerificationDetailComponent implements OnInit {
  @Input({ required: true }) rider!: Rider;
  @Output() closed = new EventEmitter<void>();
  @Output() updated = new EventEmitter<void>();

  private riderService = inject(RiderService);
  private snackbar = inject(SnackbarService);
  private modalService = inject(ModalService);
  private envInjector = inject(EnvironmentInjector);

  documents = signal<RiderDocument[]>([]);
  isLoadingDocs = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);
  selectedImage = signal<string | null>(null);

  // Rejection Modal
  showRejectModal = signal<boolean>(false);
  rejectionReason = signal<string>('');

  expectedDocTypes = [
    { key: 'selfie', label: 'Selfie / Profile' },
    { key: 'aadhaar_front', label: 'Aadhaar (Front)' },
    { key: 'aadhaar_back', label: 'Aadhaar (Back)' },
    { key: 'pan', label: 'PAN Card' },
    { key: 'driving_license', label: 'Driving License' },
    { key: 'vehicle_rc', label: 'Vehicle RC' },
    { key: 'insurance', label: 'Insurance' }
  ];

  ngOnInit() {
     this.loadDocs();
  }

  loadDocs() {
     this.isLoadingDocs.set(true);
     this.riderService.getRiderDocuments(this.rider.id).subscribe({
        next: (docs) => {
           this.documents.set(docs);
           this.isLoadingDocs.set(false);
        },
        error: () => this.isLoadingDocs.set(false)
     })
  }

  getDoc(type: string): RiderDocument | undefined {
     return this.documents().find(d => (d.doc_type || '').toLowerCase() === type.toLowerCase());
  }

  openImage(url: string) {
    this.selectedImage.set(url);
  }

  approve() {
     this.modalService.open(ConfirmModalComponent, {
        modalData: {
           heading: 'Approve Rider',
           detail: 'Are you sure you want to approve this rider? They will be activated and can start working immediately.',
           itemName: this.rider.full_name,
           btnText: 'Approve & Activate',
           btnType: 'primary'
        }
     }, this.envInjector).subscribe(result => {
        if (!result) return;

        this.isSubmitting.set(true);
        this.riderService.updateRegistrationStatus(this.rider.id, 'approved').subscribe({
           next: () => {
              this.snackbar.success('Rider approved and activated');
              this.updated.emit();
              this.isSubmitting.set(false);
              this.closed.emit();
           },
           error: () => {
             this.snackbar.error('Approval failed');
             this.isSubmitting.set(false);
           }
        })
     });
  }

  reject() {
    this.rejectionReason.set('');
    this.showRejectModal.set(true);
  }

  confirmReject() {
    const reason = this.rejectionReason().trim();
    if (!reason) return;

    this.isSubmitting.set(true);
    this.riderService.updateRegistrationStatus(this.rider.id, 'rejected', reason).subscribe({
       next: () => {
          this.snackbar.warning('Application rejected');
          this.updated.emit();
          this.isSubmitting.set(false);
          this.showRejectModal.set(false);
          this.closed.emit();
       },
       error: () => {
          this.snackbar.error('Update failed');
          this.isSubmitting.set(false);
       }
    })
  }
}



